import React, { useEffect, useRef, useState } from "react";
import { AiOutlineTable, AiOutlineDown } from "react-icons/ai";
import { cargarPrefs, guardarPrefs } from "../utils/prefsLocales";

// La lógica de storage vive en `utils/prefsLocales`, compartida con el selector de eventos de
// auditoría (#162). La clave que se le pasa incluye el namespace, así que las claves reales no
// cambian: `columns.doctree` sigue siendo `boveda.columns.doctree` y nadie pierde su elección.
const claveDe = (storageKey) => (storageKey ? `columns.${storageKey}` : null);

export const loadColumnPrefs = (storageKey) => cargarPrefs(claveDe(storageKey), {});

export const saveColumnPrefs = (storageKey, prefs) => guardarPrefs(claveDe(storageKey), prefs);

/**
 * Selector de columnas opcionales de la grilla.
 *
 * Arranca con todas apagadas: nadie se encuentra la grilla cambiada sin haberlo pedido.
 * La elección se recuerda entre sesiones por `storageKey`.
 */
const ColumnSelector = ({ columns = [], enabled = {}, onChange }) => {
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);

  // Cerrar al clickear afuera
  useEffect(() => {
    if (!open) return undefined;
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  if (columns.length === 0) return null;

  const activas = columns.filter((c) => enabled[c.key]).length;

  const toggle = (key) => {
    onChange({ ...enabled, [key]: !enabled[key] });
  };

  return (
    <div style={styles.container} ref={containerRef}>
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        style={styles.button}
        aria-expanded={open}
        aria-haspopup="true"
      >
        <AiOutlineTable />
        Columnas{activas > 0 ? ` (${activas})` : ""}
        <AiOutlineDown style={styles.caret} />
      </button>

      {open && (
        <div style={styles.dropdown} role="group" aria-label="Columnas opcionales">
          {columns.map((col) => (
            <label key={col.key} style={styles.option}>
              <input
                type="checkbox"
                checked={!!enabled[col.key]}
                onChange={() => toggle(col.key)}
              />
              <span>{col.label}</span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
};

const styles = {
  container: {
    position: "relative",
    display: "inline-block",
  },
  button: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    padding: "8px 12px",
    backgroundColor: "#fff",
    color: "#00468B",
    border: "1px solid #00468B",
    borderRadius: "4px",
    cursor: "pointer",
    fontSize: "14px",
  },
  caret: {
    fontSize: "11px",
  },
  dropdown: {
    position: "absolute",
    top: "calc(100% + 4px)",
    right: 0,
    zIndex: 20,
    minWidth: "220px",
    backgroundColor: "#fff",
    border: "1px solid #ddd",
    borderRadius: "4px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
    padding: "8px",
    display: "flex",
    flexDirection: "column",
    gap: "6px",
  },
  option: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    fontSize: "14px",
    cursor: "pointer",
    padding: "4px",
  },
};

export default ColumnSelector;
